import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  FaUpload,
  FaCode,
  FaSpinner,
  FaCheckCircle,
  FaTrash,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import useAxiosInstance from "../../hooks/useAxios";

const AddAktaTool = () => {
  const { toolName: paramToolName } = useParams();
  const axiosInstance = useAxiosInstance();

  const [toolName, setToolName] = useState(paramToolName || "");
  const [htmlFile, setHtmlFile] = useState(null);
  const [htmlFileName, setHtmlFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const [tools, setTools] = useState([]);
  const [loadingTools, setLoadingTools] = useState(false);
  const [deleteTool, setDeleteTool] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTools = async () => {
    setLoadingTools(true);
    try {
      const res = await axiosInstance.get("/wkl/akta_tools/");
      setTools(Array.isArray(res.data) ? res.data : res.data?.results || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load tools");
    } finally {
      setLoadingTools(false);
    }
  };

  useEffect(() => {
    fetchTools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async () => {
    if (!deleteTool) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/wkl/akta_tools/${deleteTool.id}/`);
      toast.success("Tool deleted successfully");
      setTools((prev) => prev.filter((t) => t.id !== deleteTool.id));
      setDeleteTool(null);
      fetchTools();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete tool");
    } finally {
      setDeleting(false);
    }
  };

  const routeSlug = toolName.trim() || "<route-name>";
  const previewUrl = `playground.akta.pro/tool/${routeSlug}`;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith(".html")) {
      toast.error("Only .html files are accepted");
      e.target.value = "";
      return;
    }
    setHtmlFile(file);
    setHtmlFileName(file.name);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!toolName.trim()) {
      toast.error("Route name is required");
      return;
    }
    if (!htmlFile) {
      toast.error("HTML file is required");
      return;
    }

    try {
      // Step 1: upload the HTML file and get back its URL
      setUploading(true);
      const formData = new FormData();
      formData.append("files", htmlFile);
      formData.append("file_name", htmlFile.name);
      formData.append("type", "file");

      const uploadRes = await axiosInstance.post(
        "/assets/upload_files_for_email_sender/",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const htmlFilePath = uploadRes.data.path;
      setUploading(false);

      // Step 2: register the tool
      setSubmitting(true);
      await axiosInstance.post("/wkl/akta_tools/", {
        name: toolName.trim(),
        html_file_path: htmlFilePath,
        route: toolName.trim(),
        meta: {},
      });

      toast.success("Tool added successfully");
      setToolName("");
      setHtmlFile(null);
      setHtmlFileName("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchTools();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to add tool");
    } finally {
      setUploading(false);
      setSubmitting(false);
    }
  };

  const isBusy = uploading || submitting;

  const buttonLabel = uploading
    ? "Uploading file..."
    : submitting
      ? "Registering tool..."
      : "Add Tool";

  return (
    <div className="max-w-4xl p-5">
      <h1 className="mb-1 text-2xl font-semibold">Add New Akta Tool</h1>
      <p className="mb-6 text-sm text-gray-500">
        Deploy an HTML tool to the Akta playground.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Route name */}
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Route Name
          </label>
          <input
            type="text"
            value={toolName}
            onChange={(e) =>
              setToolName(e.target.value.replace(/\s+/g, "-").toLowerCase())
            }
            placeholder="my-tool"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          {/* Live preview */}
          <div className="flex items-center gap-2 px-3 py-2 mt-2 border border-gray-200 rounded-lg bg-gray-50">
            <FaCode className="text-gray-400 shrink-0" />
            <span className="font-mono text-sm text-gray-600 break-all">
              {previewUrl}
            </span>
          </div>
        </div>

        {/* HTML file */}
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            HTML File
          </label>
          <div
            onClick={() => !isBusy && fileInputRef.current?.click()}
            className={`w-full border-2 border-dashed rounded-lg px-4 py-6 flex flex-col items-center gap-2 transition-colors ${
              isBusy
                ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                : "border-gray-300 cursor-pointer hover:border-blue-400 hover:bg-blue-50"
            }`}
          >
            {htmlFileName ? (
              <>
                <FaCheckCircle className="text-xl text-green-500" />
                <span className="text-sm font-medium text-green-600">
                  {htmlFileName}
                </span>
                {!isBusy && (
                  <span className="text-xs text-gray-400">
                    Click to change file
                  </span>
                )}
              </>
            ) : (
              <>
                <FaUpload className="text-xl text-gray-400" />
                <span className="text-sm text-gray-500">
                  Click to select an <strong>.html</strong> file
                </span>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".html"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        <button
          type="submit"
          disabled={isBusy}
          className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isBusy && <FaSpinner className="animate-spin" />}
          {buttonLabel}
        </button>
      </form>

      {/* Tools list */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">All Tools</h2>
          {loadingTools && <FaSpinner className="text-gray-400 animate-spin" />}
        </div>

        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 bg-gray-50">
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Route</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tools.length === 0 && !loadingTools ? (
                <tr>
                  <td
                    colSpan={3}
                    className="px-4 py-6 text-center text-gray-400"
                  >
                    No tools yet.
                  </td>
                </tr>
              ) : (
                tools.map((tool) => (
                  <tr key={tool.id} className="border-t border-gray-100">
                    <td className="px-4 py-2 font-medium text-gray-800">
                      {tool.name}
                    </td>
                    <td className="px-4 py-2">
                      <a
                        href={`https://playground.akta.pro/insights/${tool.route}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 font-mono text-gray-600 hover:text-blue-600"
                      >
                        {tool.route}
                        <FaExternalLinkAlt className="text-[10px]" />
                      </a>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => setDeleteTool(tool)}
                        className="p-1.5 rounded text-gray-400 hover:bg-gray-100 hover:text-red-500"
                        title="Delete tool"
                      >
                        <FaTrash className="text-sm" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirmation */}
      {!!deleteTool && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="w-full max-w-sm p-6 bg-white rounded-lg shadow-xl">
            <h2 className="mb-2 text-lg font-semibold">Delete tool?</h2>
            <p className="mb-4 text-sm text-gray-500">
              This will permanently delete{" "}
              <span className="font-medium text-gray-700">
                {deleteTool.name}
              </span>{" "}
              — this action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTool(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {deleting && <FaSpinner className="animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddAktaTool;
